import { track, Identify, identify, setUserId } from '@amplitude/analytics-browser';
import LogManager from '../LogManager';

const AMPLITUDE_LOCALSTORAGE_KEY = {
  HAS_SENT_WATCH_LIVE_EVENT: 'hasSentWatchLiveEvent',
}

export class AmplitudeEventManager {
  static appVersion = null;
  static mode = null;
  static jid = null;
  static signInChannel = null;
  static registerDate = null;
  static liveConnectionLength = -1;
  static relayServer = null;
  static liveConnectionTime = -1;
  static cameraId = null;
  static cameraName = null;

  resetUser () {
    setUserId(null);
  }

  resetLiveProperties () {
    AmplitudeEventManager.liveConnectionLength = -1;
    AmplitudeEventManager.liveConnectionTime = -1;
    AmplitudeEventManager.relayServer = null;
  }

  setSignInChannel (signInChannel) {
    AmplitudeEventManager.signInChannel = signInChannel;
  }

  setResgisterDate (registerDate) { 
    AmplitudeEventManager.registerDate = registerDate;
  }

  setUserProperties ({ uniqueId, subscriber, cameraCount, acOwner }) {
    const event = new Identify();

    if (uniqueId) {
      // for QA testing, need to remove before go to stage
      console.log('Amplitude set user properties: ', `uniqueId: ${uniqueId}, subscriber: ${subscriber}, sign_in_channel: ${AmplitudeEventManager.signInChannel}, register_date: ${AmplitudeEventManager.registerDate}`)
      setUserId(uniqueId);
      event.set('subscriber', subscriber).set('sign_in_channel', AmplitudeEventManager.signInChannel).set('register_date', AmplitudeEventManager.registerDate);
    }
    
    if (cameraCount || acOwner) {
      console.log('Amplitude set user properties: ', `camera_count: ${cameraCount}, ac_owner: ${acOwner}`)
      event.set('camera_count', cameraCount).set('ac_owner', acOwner);
    }
    
    identify(event);
  }

  setDefaultEventProperties ({ appVersion, mode, jid }) {
    if (appVersion) {
      AmplitudeEventManager.appVersion = appVersion;
    }
    
    if (mode) {
      AmplitudeEventManager.mode = mode;
    }

    if (jid) {
      AmplitudeEventManager.jid = jid;
    }
  }

  setUserUniqueId ({ uniqueId }) {
    setUserId(uniqueId);
  }

  sendEvent (eventName, properties) {
    // after sign in will set appVerions, mode and jid
    const combinedProperties = {
      ...properties,
      app_version: AmplitudeEventManager.appVersion,
      mode: AmplitudeEventManager.mode,
      jid: AmplitudeEventManager.jid
    }
    track(eventName, combinedProperties);
    const logManager = LogManager.getInstance();
    logManager.log(`[AmplitudeEvent] ${eventName}`, combinedProperties);
  }

  signInPageLanded (appVersion) {
    const logManager = LogManager.getInstance();
    logManager.log('[AmplitudeEvent] pageview: landing page', { app_version: appVersion, mode: AmplitudeEventManager.mode });
    track('pageview: landing page', { app_version: appVersion, mode: AmplitudeEventManager.mode });
  }

  signInSuccess ({ success, method, isSignup = false }) {
    const signInChannel = method || AmplitudeEventManager.signInChannel;
    const properties = {
      success,
      method: signInChannel,
      is_signup: isSignup
    }
    this.sendEvent('onboard: login', properties);
  }

  upgradeToSubscriber ({ productId, success }) {
    const properties = { product_id: productId, success };
    this.sendEvent('convert: upgrade to subscriber', properties);
  }

  paymentPageLanded ({ from, paymentUrl }) {
    const properties = {
      from,
      url: paymentUrl.replace(/^https:\/\//, '')
    }
    this.sendEvent('pageview: subscription', properties);
  }

  watchRecordedVideo ({ cameraId, cameraName, playerType, byAlert, timestamp }) {
    const properties = {
      cam_id: cameraId,
      cam_name: cameraName,
      player_type: playerType,
      by_alert: byAlert,
      timestamp
    }
    this.sendEvent('viewer: watch recorded video', properties);
  }

  watchLive ({ reason }) {
    const properties = {
      cam_id: AmplitudeEventManager.cameraId,
      cam_name: AmplitudeEventManager.cameraName,
      connection_time:
        AmplitudeEventManager.liveConnectionTime > 0
          ? AmplitudeEventManager.liveConnectionTime
          : -1,
      by_alert: false,
      length: AmplitudeEventManager.liveConnectionLength,
      reason,
      relay: AmplitudeEventManager.relayServer,
      viewer_count: 1, // webviewer not support multiple viewer
    };
    const hasSentWatchLiveEvent = localStorage.getItem(AMPLITUDE_LOCALSTORAGE_KEY.HAS_SENT_WATCH_LIVE_EVENT);
    if (!hasSentWatchLiveEvent) {
      this.sendEvent('viewer: watch live', properties);
      localStorage.setItem(AMPLITUDE_LOCALSTORAGE_KEY.HAS_SENT_WATCH_LIVE_EVENT, 'true');
    }
  }

  setLiveCameraInfo (cameraId, cameraName) {
    AmplitudeEventManager.cameraId = cameraId;
    AmplitudeEventManager.cameraName = cameraName;
  }

  registeredCamera ({ success }) {
    const properties = {
      success
    }
    this.sendEvent('camera: registered a camera', properties);
  }

  adBlockerDetected () {
    this.sendEvent('pageview: ads blocker detected');
  }
}

const amplitudeEventManager = new AmplitudeEventManager();

export default amplitudeEventManager;